/**
 * 담당 학생 페이지 (강사용)
 * - 강사에게 할당된 학생 목록 표시
 * - 학생별 상세 정보 및 채점 현황 보기
 */

import React, { useState, useEffect } from 'react';
import { Table, Card, Button, Tag, message, Space, Empty } from 'antd';
import { UserOutlined, FileTextOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import type { ColumnsType } from 'antd/es/table';
import { userService } from '../../services/userService';
import type { User } from '../../types/user';

/**
 * MyStudentsPage 컴포넌트
 * 강사의 담당 학생 목록 페이지
 */
export const MyStudentsPage: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [students, setStudents] = useState<User[]>([]);

  /**
   * 담당 학생 목록 조회
   */
  const fetchMyStudents = async () => {
    setLoading(true);
    try {
      const data = await userService.getMyStudents();
      setStudents(data);
    } catch (error: any) {
      message.error('담당 학생 목록 조회 실패');
      console.error('Failed to fetch my students:', error);
    } finally {
      setLoading(false);
    }
  };

  /**
   * 초기 로드
   */
  useEffect(() => {
    fetchMyStudents();
  }, []);

  /**
   * 학생 상세 정보 보기
   */
  const handleViewDetails = (studentId: string) => {
    navigate(`/grading`);
  };

  /**
   * 학생 채점 현황 보기
   */
  const handleViewGrading = (studentId: string) => {
    navigate(`/grading`);
  };

  /**
   * 테이블 컬럼 정의
   */
  const columns: ColumnsType<User> = [
    {
      title: '학생명',
      dataIndex: 'name',
      key: 'name',
      render: (name: string) => (
        <Space>
          <UserOutlined />
          <span>{name}</span>
        </Space>
      ),
    },
    {
      title: '이메일',
      dataIndex: 'email',
      key: 'email',
    },
    {
      title: '전화번호',
      dataIndex: 'phone',
      key: 'phone',
      render: (phone: string) => phone || '-',
    },
    {
      title: '상태',
      dataIndex: 'is_active',
      key: 'is_active',
      render: (isActive: boolean) => (
        <Tag color={isActive ? 'green' : 'red'}>
          {isActive ? '활성' : '비활성'}
        </Tag>
      ),
    },
    {
      title: '가입일',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date: string) => new Date(date).toLocaleDateString('ko-KR'),
    },
    {
      title: '액션',
      key: 'action',
      render: (_: any, record: User) => (
        <Space>
          <Button
            size="small"
            icon={<FileTextOutlined />}
            onClick={() => handleViewGrading(record.id)}
          >
            채점 현황
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      <Card
        title={
          <Space>
            <UserOutlined />
            <span>담당 학생</span>
          </Space>
        }
        extra={
          <Button onClick={fetchMyStudents} loading={loading}>
            새로고침
          </Button>
        }
      >
        {students.length === 0 && !loading ? (
          <Empty
            description="할당된 학생이 없습니다"
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        ) : (
          <Table
            columns={columns}
            dataSource={students}
            rowKey="id"
            loading={loading}
            pagination={{
              pageSize: 20,
              showTotal: (total) => `총 ${total}명`,
            }}
          />
        )}
      </Card>
    </div>
  );
};

export default MyStudentsPage;
